"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  AuthError,
} from "firebase/auth";
import { auth } from "./firebase";
import { getClientUserByEmail, updateClientUser } from "./services/firestore";
import { ClientUser } from "./types/admin";

interface ClientAuthContextType {
  user: User | null;
  clientUser: ClientUser | null;
  loading: boolean;
  signingIn: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined);

// Helper to get user-friendly error messages
function getAuthErrorMessage(error: AuthError): string {
  switch (error.code) {
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
      return "Incorrect password.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Network error. Please check your internet connection.";
    default:
      console.error("Auth error code:", error.code, error.message);
      return "Failed to sign in. Please try again.";
  }
}

export function ClientAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [clientUser, setClientUser] = useState<ClientUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load client user data from Firestore
  const loadClientUser = async (email: string): Promise<ClientUser | null> => {
    try {
      const client = await getClientUserByEmail(email);
      if (client && client.status !== 'disabled') {
        setClientUser(client);
        return client;
      }
      return null;
    } catch (err) {
      console.error("Error loading client user:", err);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser?.email) {
        // Check if this is a client user (not an admin)
        const client = await loadClientUser(firebaseUser.email);
        if (!client) {
          // Not a client user - could be admin or unauthorized
          setClientUser(null);
        }
      } else {
        setClientUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string): Promise<boolean> => {
    setError(null);
    setSigningIn(true);
    
    try {
      // Sign in with Firebase Auth first
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Now check if this email is a registered client user
      const client = await getClientUserByEmail(email);
      if (!client) {
        // Not a client user - sign them out
        await firebaseSignOut(auth);
        setError("This email is not registered as a client user.");
        setSigningIn(false);
        return false;
      }
      
      if (client.status === 'disabled') {
        // Account disabled - sign them out
        await firebaseSignOut(auth);
        setError("Your account has been disabled. Please contact your administrator.");
        setSigningIn(false);
        return false;
      }

      // Update last login time
      if (result.user.email) {
        await updateClientUser(client.id, {
          lastLoginAt: new Date(),
          status: 'active',
        });
        
        // Refresh client user data
        const updatedClient = await getClientUserByEmail(result.user.email);
        if (updatedClient) {
          setClientUser(updatedClient);
        }
      }

      setSigningIn(false);
      return true;
    } catch (err) {
      console.error("Sign in error:", err);
      const authError = err as AuthError;
      setError(getAuthErrorMessage(authError));
      setSigningIn(false);
      return false;
    }
  };

  // Sign out
  const signOut = async () => {
    await firebaseSignOut(auth);
    setClientUser(null);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return (
    <ClientAuthContext.Provider
      value={{
        user,
        clientUser,
        loading,
        signingIn,
        error,
        signIn,
        signOut,
        clearError,
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext);
  if (context === undefined) {
    throw new Error("useClientAuth must be used within a ClientAuthProvider");
  }
  return context;
}
